import type { BillingInterval, PlanId } from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { extractErrorMessage } from '@/api/extract-error'

import {
  cancelSubscription,
  changePlan,
  createCheckoutSession,
  createPortalSession,
  resumeSubscription
} from './api'
import { BILLING_OVERVIEW_KEY } from './keys'
import { suppressNextSubscriptionToast } from './socket-handlers'
import { goToStripe } from './stripe-tab'

type PlanChoice = { planId: PlanId; interval: BillingInterval }

/* the tab must be opened synchronously inside the click handler, so the caller
 * opens a blank one and hands it over for the url that arrives later. safari
 * and firefox block a window.open that happens after an await. */
type CheckoutChoice = PlanChoice & { tab?: Window | null }

export function usePlanActions(options?: { onInvoiceSent?: () => void }) {
  const queryClient = useQueryClient()

  /* entitlements ride setup state, so both caches have to drop after any change
   * or the page keeps rendering the old plan. */
  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: BILLING_OVERVIEW_KEY }),
      queryClient.invalidateQueries({ queryKey: ['setup'] })
    ])
  }

  const checkout = useMutation({
    mutationFn: ({ planId, interval }: CheckoutChoice) =>
      createCheckoutSession(planId, interval),
    onSuccess: ({ url, mode }, { tab }) => {
      if (mode === 'checkout') {
        tab?.close()
        window.location.href = url
        return
      }
      goToStripe(url, tab)
      options?.onInvoiceSent?.()
    },
    onError: (error, { tab }) => {
      tab?.close()
      toast.error(extractErrorMessage(error))
    }
  })

  /* every mutation below also lands as a realtime broadcast, which would toast
   * a second time in this tab. claim that broadcast before firing. */
  const change = useMutation({
    mutationFn: ({ planId, interval }: PlanChoice) => {
      suppressNextSubscriptionToast()
      return changePlan(planId, interval)
    },
    onSuccess: async result => {
      await refresh()
      if (result.applied === 'unchanged') {
        toast.info('You are already on this plan')
        return
      }
      if (result.applied === 'schedule_released') {
        toast.success('Scheduled change cancelled')
        return
      }
      toast.success(
        result.applied === 'scheduled' && result.effectiveAt
          ? `Plan changes on ${new Date(result.effectiveAt).toLocaleDateString()}`
          : 'Plan updated'
      )
    },
    onError: error => toast.error(extractErrorMessage(error))
  })

  const cancel = useMutation({
    mutationFn: () => {
      suppressNextSubscriptionToast()
      return cancelSubscription()
    },
    onSuccess: async () => {
      await refresh()
      toast.success('Subscription will end at the close of this period')
    },
    onError: error => toast.error(extractErrorMessage(error))
  })

  const resume = useMutation({
    mutationFn: () => {
      suppressNextSubscriptionToast()
      return resumeSubscription()
    },
    onSuccess: async () => {
      await refresh()
      toast.success('Subscription resumed')
    },
    onError: error => toast.error(extractErrorMessage(error))
  })

  /* opens in a second tab so this one stays mounted: the portal can cancel, and
   * the resulting webhook updates this page live. */
  const portal = useMutation({
    mutationFn: (_input: { tab?: Window | null }) => createPortalSession(),
    onSuccess: ({ url }, { tab }) => {
      goToStripe(url, tab)
    },
    onError: (error, { tab }) => {
      tab?.close()
      toast.error(extractErrorMessage(error))
    }
  })

  return { checkout, change, cancel, resume, portal }
}
